import React, { useState } from 'react';

const SurveyForm = ({ survey, language, onComplete }) => {
    const [answers, setAnswers] = useState({});
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);

    const translations = {
        submit: {
            zh: '提交问卷',
            en: 'Submit Survey'
        },
        required: {
            zh: '此题为必答题',
            en: 'This question is required'
        }
    };

    // 处理输入变化
    const handleInputChange = (questionId, value, questionType) => {
        setAnswers(prev => {
            if (questionType === 'multiple_choice') {
                // 对于多选题，需要特殊处理
                const currentValues = prev[questionId] || [];
                if (Array.isArray(currentValues)) {
                    if (currentValues.includes(value)) {
                        return {
                            ...prev,
                            [questionId]: currentValues.filter(v => v !== value)
                        };
                    } else {
                        return {
                            ...prev,
                            [questionId]: [...currentValues, value]
                        };
                    }
                }
            }
            
            return {
                ...prev,
                [questionId]: value
            };
        });
        
        // 清除错误
        if (errors[questionId]) {
            setErrors(prev => ({
                ...prev,
                [questionId]: null
            }));
        }
    };

    // 验证表单
    const validateForm = () => {
        const newErrors = {};
        let isValid = true;
        
        survey.questions.forEach(question => {
            const answer = answers[question.id];
            
            if (!answer || (Array.isArray(answer) && answer.length === 0)) {
                newErrors[question.id] = translations.required[language];
                isValid = false;
            }
        });
        
        setErrors(newErrors);
        return isValid;
    };

    // 处理表单提交
    const handleSubmit = (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }
        
        setLoading(true);
        
        // 格式化答案数据
        const formattedAnswers = Object.keys(answers).map(questionId => {
            const question = survey.questions.find(q => q.id === parseInt(questionId));
            
            if (question.question_type === 'multiple_choice') {
                // 多选题，返回多个选项ID
                return answers[questionId].map(optionId => ({
                    question_id: parseInt(questionId),
                    option_id: parseInt(optionId)
                }));
            } else if (question.question_type === 'single_choice') {
                // 单选题，返回单个选项ID
                return {
                    question_id: parseInt(questionId),
                    option_id: parseInt(answers[questionId])
                };
            } else {
                // 自由文本题，返回文本内容
                return {
                    question_id: parseInt(questionId),
                    freetext_answer: answers[questionId]
                };
            }
        });
        
        // 扁平化数组（因为多选题会返回数组的数组）
        const flatAnswers = formattedAnswers.flat();
        
        // 调用完成回调
        onComplete(flatAnswers);
        setLoading(false);
    };

    return (
        <form onSubmit={handleSubmit}>
            {survey.questions.map((question, index) => (
                <div key={question.id} className="mb-4">
                    <h5 className="mb-3">
                        {index + 1}. {language === 'zh' ? question.question_text_zh : question.question_text_en}
                    </h5>
                    
                    {errors[question.id] && (
                        <div className="alert alert-danger py-2">{errors[question.id]}</div>
                    )}
                    
                    {question.question_type === 'freetext' ? (
                        <textarea
                            className="form-control"
                            rows="3"
                            value={answers[question.id] || ''}
                            onChange={(e) => handleInputChange(question.id, e.target.value, question.question_type)}
                        ></textarea>
                    ) : question.question_type === 'single_choice' ? (
                        <div className="options">
                            {question.options.map(option => (
                                <div className="form-check mb-2" key={option.id}>
                                    <input
                                        className="form-check-input"
                                        type="radio"
                                        name={`question_${question.id}`}
                                        id={`option_${option.id}`}
                                        value={option.id}
                                        checked={answers[question.id] === option.id.toString()}
                                        onChange={(e) => handleInputChange(question.id, e.target.value, question.question_type)}
                                    />
                                    <label className="form-check-label" htmlFor={`option_${option.id}`}>
                                        {language === 'zh' ? option.option_text_zh : option.option_text_en}
                                    </label>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="options">
                            {question.options.map(option => (
                                <div className="form-check mb-2" key={option.id}>
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id={`option_${option.id}`}
                                        value={option.id}
                                        checked={(answers[question.id] || []).includes(option.id.toString())}
                                        onChange={(e) => handleInputChange(question.id, e.target.value, question.question_type)}
                                    />
                                    <label className="form-check-label" htmlFor={`option_${option.id}`}>
                                        {language === 'zh' ? option.option_text_zh : option.option_text_en}
                                    </label>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            ))}
            
            <div className="text-center">
                <button 
                    type="submit" 
                    className="btn btn-primary"
                    disabled={loading}
                >
                    {loading ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            {translations.submit[language]}
                        </>
                    ) : translations.submit[language]}
                </button>
            </div>
        </form>
    );
};

export default SurveyForm; 